type DiagnosticSample = {
  codec?: string;
  lossPercent?: number | null;
  rttMs?: number | null;
  bitrate?: number | null;
  actualFps?: number | null;
  targetFps?: number | null;
  encodeMsPerFrame?: number | null;
  availableOutgoingBitrate?: number | null;
  bitrateCap?: number | null;
  actualBitrate?: number | null;
  qualityLimitationReason?: string | null;
};

function check(label: string, status: string, value: string) {
  return { label, status, value };
}

function number(value: number | null | undefined) {
  return Number.isFinite(value) ? value : null;
}

function mbps(bits: number | null | undefined) {
  if (!number(bits)) return "unavailable";
  const value = bits! / 1_000_000;
  return `${value >= 10 || Number.isInteger(value) ? value.toFixed(0) : value.toFixed(1)} Mbps`;
}

export function connectionQualityMeterLevel(severity: string) {
  if (severity === "good") return 4;
  if (severity === "warning") return 3;
  if (severity === "critical") return 1;
  return 0;
}

function networkQualityChecks(sample: DiagnosticSample, throughputLabel: string) {
  const lossPercent = number(sample.lossPercent);
  const rttMs = number(sample.rttMs);
  const bitrate = number(sample.bitrate);

  return [
    check(
      "Latency",
      rttMs == null ? "unknown" : rttMs >= 300 ? "bad" : rttMs >= 150 ? "warn" : "good",
      rttMs == null ? "Collecting" : `${Math.round(rttMs)} ms`,
    ),
    check(
      "Packet loss",
      lossPercent == null
        ? "unknown"
        : lossPercent >= 5
          ? "bad"
          : lossPercent >= 2
            ? "warn"
            : "good",
      lossPercent == null ? "Collecting" : `${lossPercent.toFixed(1)}% loss`,
    ),
    check(
      throughputLabel,
      bitrate! > 0 ? "info" : "unknown",
      bitrate! > 0 ? mbps(bitrate) : "Collecting",
    ),
  ];
}

export function receiverQualityChecks(sample: DiagnosticSample) {
  return networkQualityChecks(sample, "Receive rate");
}

export function publisherQualityChecks(sample: DiagnosticSample) {
  return networkQualityChecks(sample, "Upload rate");
}

function networkSeverity(sample: DiagnosticSample) {
  const lossPercent = number(sample.lossPercent);
  const rttMs = number(sample.rttMs);
  const hasMeasurement = lossPercent != null || rttMs != null;
  if (!hasMeasurement) return "info";
  if ((lossPercent != null && lossPercent >= 5) || (rttMs != null && rttMs >= 300))
    return "critical";
  if ((lossPercent != null && lossPercent >= 2) || (rttMs != null && rttMs >= 150))
    return "warning";
  return "good";
}

export function diagnosePublisherConnection(sample: DiagnosticSample) {
  const lossPercent = number(sample.lossPercent);
  const rttMs = number(sample.rttMs);
  const severity = networkSeverity(sample);
  const checks = publisherQualityChecks(sample);
  if (severity === "info") {
    return {
      code: "publisher-gathering",
      severity,
      title: "Collecting LiveKit uplink measurements",
      message: "WebRTC is still collecting statistics for the publishing connection.",
      checks,
    };
  }
  if (severity === "warning" || severity === "critical") {
    return {
      code: "publisher-network",
      severity,
      title: "LiveKit uplink is unstable",
      message: `The publishing path reports ${lossPercent == null ? "unknown loss" : `${lossPercent.toFixed(1)}% packet loss`} and ${rttMs == null ? "unknown latency" : `${Math.round(rttMs)} ms RTT`}.`,
      checks,
    };
  }
  return {
    code: "publisher-healthy",
    severity,
    title: "LiveKit uplink is healthy",
    message: "Packet loss and latency to LiveKit are within the diagnostic thresholds.",
    checks,
  };
}

export function diagnoseReceiverConnection(sample: DiagnosticSample) {
  const lossPercent = number(sample.lossPercent);
  const rttMs = number(sample.rttMs);
  const checks = receiverQualityChecks(sample);
  const severity = networkSeverity(sample);
  const hasMeasurement = severity !== "info";
  const degraded = severity === "warning" || severity === "critical";

  if (!hasMeasurement) {
    return {
      code: "gathering",
      severity: "info",
      title: "Collecting measurements",
      message:
        "Keep connection details open for a few seconds while WebRTC reports network statistics.",
      checks,
    };
  }
  if (degraded) {
    return {
      code: "network",
      severity,
      title: "Viewer network path is unstable",
      message: `This path reports ${lossPercent == null ? "unknown loss" : `${lossPercent.toFixed(1)}% packet loss`} and ${rttMs == null ? "unknown latency" : `${Math.round(rttMs)} ms RTT`}.`,
      checks,
    };
  }
  return {
    code: "healthy",
    severity: "good",
    title: "No network problem detected",
    message: "Packet loss and latency are within the diagnostic thresholds.",
    checks,
  };
}

export function diagnoseConnection(sample: DiagnosticSample) {
  const actualFps = number(sample.actualFps);
  const targetFps = number(sample.targetFps);
  const fpsRatio = actualFps != null && targetFps ? actualFps / targetFps : null;
  const lossPercent = number(sample.lossPercent);
  const rttMs = number(sample.rttMs);
  const encodeMsPerFrame = number(sample.encodeMsPerFrame);
  const frameBudgetMs = targetFps ? 1000 / targetFps : null;
  const encodeUtilization =
    encodeMsPerFrame != null && frameBudgetMs ? encodeMsPerFrame / frameBudgetMs : null;
  const availableBitrate = number(sample.availableOutgoingBitrate);
  const bitrateCap = number(sample.bitrateCap);
  const actualBitrate = number(sample.actualBitrate);
  const qualityReason = sample.qualityLimitationReason || "none";

  const checks = [
    check(
      "Frame rate",
      fpsRatio == null ? "unknown" : fpsRatio < 0.5 ? "bad" : fpsRatio < 0.8 ? "warn" : "good",
      actualFps == null
        ? "Collecting"
        : `${Math.round(actualFps!)} / ${Math.round(targetFps!)} fps`,
    ),
    check(
      "Encoder",
      qualityReason === "cpu" || (encodeUtilization != null && encodeUtilization >= 0.85)
        ? "bad"
        : encodeUtilization == null
          ? "unknown"
          : "good",
      qualityReason === "cpu"
        ? "CPU limited"
        : encodeMsPerFrame == null
          ? "Collecting"
          : `${encodeMsPerFrame.toFixed(1)} ms/frame`,
    ),
    check(
      "Upload capacity",
      qualityReason === "bandwidth" ||
        (availableBitrate && bitrateCap && availableBitrate < bitrateCap * 0.8)
        ? "bad"
        : availableBitrate == null
          ? "unknown"
          : "good",
      availableBitrate == null ? "Unavailable" : `${mbps(availableBitrate)} available`,
    ),
    check(
      "Viewer path",
      (lossPercent != null && lossPercent >= 5) || (rttMs != null && rttMs >= 300)
        ? "bad"
        : (lossPercent != null && lossPercent >= 2) || (rttMs != null && rttMs >= 150)
          ? "warn"
          : lossPercent == null && rttMs == null
            ? "unknown"
            : "good",
      `${lossPercent == null ? "—" : `${lossPercent.toFixed(1)}% loss`} · ${rttMs == null ? "—" : `${Math.round(rttMs)} ms RTT`}`,
    ),
  ];

  const severity = fpsRatio != null && fpsRatio < 0.5 ? "critical" : "warning";
  if (
    qualityReason === "cpu" ||
    (fpsRatio != null && fpsRatio < 0.8 && encodeUtilization != null && encodeUtilization >= 0.85)
  ) {
    return {
      code: "cpu",
      severity,
      title: "Encoder is CPU-limited",
      message:
        "The browser is spending too much time encoding frames. Close CPU-heavy apps or reduce capture resolution.",
      checks,
    };
  }
  if (
    qualityReason === "bandwidth" ||
    (fpsRatio != null &&
      fpsRatio < 0.8 &&
      availableBitrate &&
      bitrateCap &&
      availableBitrate < bitrateCap * 0.8)
  ) {
    return {
      code: "bandwidth",
      severity,
      title: "Sender upload is constrained",
      message: `${mbps(availableBitrate)} is available for this path while the applied cap is ${mbps(bitrateCap)}. The browser is reducing quality because of congestion.`,
      checks,
    };
  }
  if ((lossPercent != null && lossPercent >= 2) || (rttMs != null && rttMs >= 150)) {
    return {
      code: "network",
      severity:
        (lossPercent != null && lossPercent >= 5) || (rttMs != null && rttMs >= 300)
          ? "critical"
          : "warning",
      title: "Viewer network path is unstable",
      message: `This path reports ${lossPercent == null ? "unknown loss" : `${lossPercent.toFixed(1)}% packet loss`} and ${rttMs == null ? "unknown latency" : `${Math.round(rttMs)} ms RTT`}. Other viewers may remain unaffected.`,
      checks,
    };
  }
  if (
    fpsRatio != null &&
    fpsRatio < 0.8 &&
    bitrateCap &&
    actualBitrate &&
    actualBitrate >= bitrateCap * 0.9
  ) {
    return {
      code: "configured",
      severity,
      title: "Configured bitrate cap is being reached",
      message: `The stream is using ${mbps(actualBitrate)} against its ${mbps(bitrateCap)} cap. Increase the total upload budget or accept a lower resolution.`,
      checks,
    };
  }
  if (qualityReason === "other") {
    return {
      code: "browser",
      severity: "warning",
      title: "Browser reports another quality limit",
      message:
        "The browser is limiting quality but did not attribute it to CPU or bandwidth. Keep connection details open while conditions change.",
      checks,
    };
  }
  if (fpsRatio != null && fpsRatio < 0.8) {
    return {
      code: "framerate",
      severity,
      title: "Frame rate is below target",
      message: `The stream is producing ${Math.round(actualFps!)} of ${Math.round(targetFps!)} fps, but current statistics do not yet identify CPU or bandwidth as the cause.`,
      checks,
    };
  }
  if (actualFps == null) {
    return {
      code: "gathering",
      severity: "info",
      title: "Collecting measurements",
      message:
        "Keep connection details open for a few seconds while WebRTC reports encoder and network statistics.",
      checks,
    };
  }
  return {
    code: "healthy",
    severity: "good",
    title: "No bottleneck detected",
    message:
      "Frame rate, encoder time, upload capacity, packet loss, and latency are within the diagnostic thresholds.",
    checks,
  };
}
